// Game action callbacks — extracted from App.tsx to keep the component lean.
// Each callback is a stable useCallback wrapper around sim + selection state.

import type { SceneNode } from '@aod/protocol';
import { NAVAL_KINDS } from '@aod/ui/prefabs';
import { useCallback } from 'react';
import { tryChampionCast } from './champion/castAbility.js';
import type { SimWorld } from './sim/useSimWorld/index.js';
import type { Selection } from './testBridge/useTestBridge.js';
import type { Archetype } from './ui/DoctrinePanel/index.js';

interface UseGameActionsOptions {
  sim: SimWorld;
  selection: Selection;
  setSelection: (s: Selection) => void;
  setLocalPlayerName: (name: string) => void;
}

export function useGameActions(opts: UseGameActionsOptions) {
  const { sim, selection, setSelection, setLocalPlayerName } = opts;

  const handleSetPlayerName = useCallback(
    (name: string) => {
      setLocalPlayerName(name);
      localStorage.setItem('aod_player_name', name);
    },
    [setLocalPlayerName],
  );

  // While a champion ability is armed, a click on any hex entity casts it there.
  const castChampion = useCallback(
    (node: SceneNode): boolean => {
      const q = node.props?.q as number | undefined;
      const r = node.props?.r as number | undefined;
      if (q === undefined || r === undefined) return false;
      return tryChampionCast({ snapshot: sim.snapshot, activate: sim.activateLegendAbility }, q, r);
    },
    [sim],
  );

  /** The unit node behind the current selection, if any (fresh from the snapshot). */
  const selectedUnit = useCallback((): SceneNode | null => {
    if (selection?.kind !== 'unit') return null;
    return (
      sim.snapshot?.scene?.children?.find((n) => n.id === selection.id && n.type === 'unit') ?? null
    );
  }, [selection, sim.snapshot]);

  const handleTileClick = useCallback(
    (tile: SceneNode) => {
      if (castChampion(tile)) return;
      const unit = selectedUnit();
      if (unit) {
        const q = tile.props?.q as number | undefined;
        const r = tile.props?.r as number | undefined;
        if (q !== undefined && r !== undefined) {
          // A ship plots a course and sails itself; a land unit steps one hex.
          const kind = unit.props?.kind as string | undefined;
          if (kind && NAVAL_KINDS.has(kind)) sim.sailShip(selection!.id, q, r);
          else sim.moveUnit(selection!.id, q, r);
        }
        return;
      }
      setSelection({ kind: 'tile', id: tile.id });
    },
    [selection, sim, setSelection, castChampion, selectedUnit],
  );

  const handleCityClick = useCallback(
    (city: SceneNode) => {
      if (castChampion(city)) return;
      // A selected ship works the harbour instead of losing the selection:
      // load at your own port, sell at a foreign one. Cities without a harbour
      // stay selectable.
      const unit = selectedUnit();
      const kind = unit?.props?.kind as string | undefined;
      const harbour = (city.props?.completedBuildings as string[] | undefined)?.includes('harbour');
      if (unit && kind && NAVAL_KINDS.has(kind) && harbour) {
        sim.dockTrade(unit.id, city.id);
        return;
      }
      setSelection({ kind: 'city', id: city.id });
    },
    [setSelection, castChampion, selectedUnit, sim],
  );

  const handleUnitClick = useCallback(
    (unit: SceneNode) => {
      if (castChampion(unit)) return;
      setSelection({ kind: 'unit', id: unit.id });
    },
    [setSelection, castChampion],
  );

  const handleBackgroundClick = useCallback(() => setSelection(null), [setSelection]);
  const closeSelection = useCallback(() => setSelection(null), [setSelection]);

  const startNewMatch = useCallback(() => {
    setSelection(null);
    sim.newMatch();
  }, [sim, setSelection]);

  const handleSave = useCallback(() => {
    try {
      const json = sim.saveGame();
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `aod-save-${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Save failed:', err);
    }
  }, [sim]);

  const handleLoad = useCallback(async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      try {
        const text = await file.text();
        JSON.parse(text);
        // Load in place. (This used to reload the page right after loading,
        // which booted a fresh match and threw the loaded game away.)
        setSelection(null);
        sim.loadGame(text);
      } catch (err) {
        console.error('Load failed:', err);
        alert('Failed to load save file');
      }
    };
    input.click();
  }, [sim, setSelection]);

  const handleStartBuilding = useCallback(
    (cityId: string, buildingId: string) => sim.startBuilding(cityId, buildingId),
    [sim],
  );

  const handleSetCityDoctrine = useCallback(
    (cityId: string, archetype: Archetype) => sim.setCityDoctrine(cityId, archetype),
    [sim],
  );

  return {
    handleSetPlayerName,
    handleTileClick,
    handleCityClick,
    handleUnitClick,
    handleBackgroundClick,
    closeSelection,
    startNewMatch,
    handleSave,
    handleLoad,
    handleStartBuilding,
    handleSetCityDoctrine,
  };
}
